import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { useMeQuery } from './engagement-lifecycle';
import { SynthesisCitationSchema } from './synthesis';
import type { PostureStatus } from '@/components/ui/posture-chip';

/**
 * The one Health & readiness report and the evidence panes' reads (E20 /
 * T-0223), mounted under `/api/v1/engagements/:engagement_id/health-readiness`:
 *
 *   GET   …/report                       — the latest report (draft or
 *                                          published), its seven sections in
 *                                          order, the verdict block and the
 *                                          module switches; `report: null`
 *                                          before the first draft. T-0245:
 *                                          plus the cover's figures, the
 *                                          placeholder words and, on a
 *                                          draft, the prose checks.
 *   POST  …/report:draft                 — both drafters into one draft; a
 *                                          redraft keeps edited sections.
 *   PATCH …/report/sections/:section_id  — one section, override tag +
 *                                          rationale required.
 *   POST  …/report:publish               — one snapshot, one PDF.
 *   GET   …/finding-options              — the vocabulary the findings
 *                                          register accepts.
 *
 * Plus the readiness capture (`POST …/readiness/snapshots`, T-0088) the
 * Snapshots & trend pane triggers, and the interview-note detail fan-out
 * the Interview notes pane reads proposal counts from.
 */

const base = (engagementId: string) =>
  `/api/v1/engagements/${encodeURIComponent(engagementId)}/health-readiness`;

const newKey = () =>
  typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `hr-${Date.now()}-${Math.random().toString(36).slice(2)}`;

// ===========================================================================
// Schemas
// ===========================================================================

export const ReportSectionKeySchema = z.enum([
  'exec_summary',
  'posture',
  'top_risks',
  'recommendations',
  'domain_rollup',
  'findings_appendix',
  'go_live_readiness',
]);
export type ReportSectionKey = z.infer<typeof ReportSectionKeySchema>;

export const ReportSectionSchema = z.object({
  section_id: z.string().uuid(),
  section_key: ReportSectionKeySchema,
  section_order: z.number(),
  title: z.string(),
  content_text: z.string(),
  citations: z.array(SynthesisCitationSchema),
  origin: z.enum(['ai_drafted', 'consultant_edited']),
  is_safe_refusal: z.boolean(),
  edited_at: z.string().nullable(),
  edited_by_user_id: z.string().nullable(),
  edit_reason_tag: z.string().nullable(),
  edit_rationale: z.string().nullable(),
});
export type ReportSection = z.infer<typeof ReportSectionSchema>;

export const RollupColorSchema = z.enum(['red', 'yellow_divergent', 'green', 'grey']);
export type RollupColor = z.infer<typeof RollupColorSchema>;

export const HealthReadinessReportSchema = z.object({
  report_id: z.string().uuid(),
  engagement_id: z.string().uuid(),
  status: z.enum(['draft', 'publishing', 'published']),
  cycle_label: z.string(),
  drafted_by_user_id: z.string(),
  drafted_at: z.string(),
  updated_at: z.string(),
  published_at: z.string().nullable(),
  published_by_user_id: z.string().nullable(),
  snapshot_id: z.string().uuid().nullable(),
  pdf_report_id: z.string().uuid().nullable(),
  pdf_path: z.string().nullable(),
  model_id: z.string().nullable(),
  prompt_version: z.string().nullable(),
  go_live_section: z.enum(['included', 'not_in_scope', 'missing']),
  sections: z.array(ReportSectionSchema),
});
export type HealthReadinessReport = z.infer<typeof HealthReadinessReportSchema>;

export const ReportVerdictSchema = z.object({
  source: z.enum(['live', 'frozen_at_publish']),
  posture: z
    .object({
      overall_rollup: RollupColorSchema,
      total_active_domains: z.number(),
      counts: z.object({
        red: z.number(),
        yellow_divergent: z.number(),
        green: z.number(),
        grey: z.number(),
        unrated: z.number(),
      }),
    })
    .nullable(),
  readiness: z
    .object({
      total_percent: z.number().nullable(),
      recommendation: z.string().nullable(),
      scored_count: z.number(),
      criteria_count: z.number(),
      thresholds: z.object({ go: z.number(), conditional: z.number() }),
    })
    .nullable(),
});
export type ReportVerdict = z.infer<typeof ReportVerdictSchema>;

/**
 * T-0245: what the cover draws besides the verdict — the composite read's
 * domains, criteria and trend; live on a draft, frozen on a published
 * report (null for one published before T-0245).
 */
export const ReportFiguresSchema = z.object({
  posture_counts: z.object({
    critical: z.number(),
    at_risk: z.number(),
    healthy: z.number(),
    unassessed: z.number(),
  }),
  domains: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      posture: z.enum(['critical', 'at_risk', 'healthy', 'unassessed']),
    }),
  ),
  criteria: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      score_percent: z.number().nullable(),
      weighted_contribution: z.number(),
    }),
  ),
  trend: z.array(
    z.object({
      snapshot_id: z.string(),
      captured_at: z.string(),
      total_percent: z.number().nullable(),
    }),
  ),
});
export type ReportFigures = z.infer<typeof ReportFiguresSchema>;

/** T-0245: a typed percentage, or a placeholder no rollup answers, in a draft section. */
export const ReportCheckSchema = z.object({
  section_id: z.string(),
  section_key: z.string(),
  title: z.string(),
  kind: z.enum(['typed_percent', 'unknown_placeholder']),
  text: z.string(),
});
export type ReportCheck = z.infer<typeof ReportCheckSchema>;

export const GetReportResultSchema = z.object({
  report: HealthReadinessReportSchema.nullable(),
  verdict: ReportVerdictSchema,
  modules: z.object({ health_check: z.boolean(), go_live_readiness: z.boolean() }),
  // Optional so a read from an API before T-0245 still parses; the pane
  // reads them as null / {} / [] then.
  figures: ReportFiguresSchema.nullable().optional(),
  /** The words each `{{placeholder}}` prints, from the rollups (the API builds them, as the PDF does). */
  placeholders: z.record(z.string()).optional(),
  checks: z.array(ReportCheckSchema).optional(),
});
export type GetReportResult = z.infer<typeof GetReportResultSchema>;

export const PublishReportResultSchema = z.object({
  report_id: z.string().uuid(),
  status: z.literal('published'),
  snapshot_id: z.string().uuid(),
  pdf_report_id: z.string().uuid(),
  pdf_path: z.string(),
  published_at: z.string(),
  verdict: ReportVerdictSchema,
});
export type PublishReportResult = z.infer<typeof PublishReportResultSchema>;

export const FindingOptionsSchema = z.object({
  finding_types: z.array(z.string()),
  severities: z.array(z.string()),
  source_kinds: z.array(z.string()),
  statuses: z.array(z.string()),
});
export type FindingOptions = z.infer<typeof FindingOptionsSchema>;

export const CaptureReadinessSnapshotResultSchema = z.object({
  snapshot_id: z.string().uuid(),
  captured_at: z.string(),
  recommendation: z.string().nullable(),
  total_percent: z.number().nullable(),
});
export type CaptureReadinessSnapshotResult = z.infer<typeof CaptureReadinessSnapshotResultSchema>;

// ===========================================================================
// Report
// ===========================================================================

export const healthReadinessReportKey = (engagementId: string | undefined) =>
  ['health-readiness-report', engagementId] as const;

export function getHealthReadinessReport(engagementId: string): Promise<GetReportResult> {
  return apiClient(`${base(engagementId)}/report`, GetReportResultSchema);
}

export function useHealthReadinessReportQuery(engagementId: string | undefined) {
  return useQuery({
    queryKey: healthReadinessReportKey(engagementId),
    queryFn: () => getHealthReadinessReport(engagementId!),
    enabled: Boolean(engagementId),
  });
}

export interface DraftReportInput {
  summary_focus?: string;
  audience?: string;
}

export function draftHealthReadinessReport(
  engagementId: string,
  input: DraftReportInput,
  idempotencyKey: string,
): Promise<GetReportResult> {
  return apiClient(`${base(engagementId)}/report:draft`, GetReportResultSchema, {
    method: 'POST',
    body: input,
    idempotencyKey,
  });
}

export function useDraftReportMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    // Fresh key per click: a network retry of this click replays, a second
    // click redrafts.
    mutationFn: (input: DraftReportInput = {}) =>
      draftHealthReadinessReport(engagementId!, input, newKey()),
    onSuccess: (result) => {
      qc.setQueryData(healthReadinessReportKey(engagementId), result);
    },
  });
}

export interface EditReportSectionInput {
  content_text: string;
  citations?: Array<z.infer<typeof SynthesisCitationSchema>>;
  override: { tag: string; rationale: string };
}

export function editReportSection(
  engagementId: string,
  sectionId: string,
  input: EditReportSectionInput,
  idempotencyKey: string,
): Promise<ReportSection> {
  return apiClient(
    `${base(engagementId)}/report/sections/${encodeURIComponent(sectionId)}`,
    ReportSectionSchema,
    { method: 'PATCH', body: input, idempotencyKey },
  );
}

export function useEditReportSectionMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sectionId, input }: { sectionId: string; input: EditReportSectionInput }) =>
      editReportSection(engagementId!, sectionId, input, newKey()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: healthReadinessReportKey(engagementId) });
    },
  });
}

export function publishHealthReadinessReport(
  engagementId: string,
  idempotencyKey: string,
): Promise<PublishReportResult> {
  return apiClient(`${base(engagementId)}/report:publish`, PublishReportResultSchema, {
    method: 'POST',
    body: {},
    idempotencyKey,
  });
}

export function usePublishReportMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => publishHealthReadinessReport(engagementId!, newKey()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: healthReadinessReportKey(engagementId) });
      void qc.invalidateQueries({ queryKey: ['snapshots', engagementId] });
    },
  });
}

// ===========================================================================
// Findings vocabulary
// ===========================================================================

export function getFindingOptions(engagementId: string): Promise<FindingOptions> {
  return apiClient(`${base(engagementId)}/finding-options`, FindingOptionsSchema);
}

export function useFindingOptionsQuery(engagementId: string | undefined) {
  return useQuery({
    queryKey: ['health-readiness', 'finding-options', engagementId] as const,
    queryFn: () => getFindingOptions(engagementId!),
    enabled: Boolean(engagementId),
    // The vocabulary changes with a deploy (or a firm-settings save), not
    // while a pane is open.
    staleTime: 10 * 60_000,
  });
}

// ===========================================================================
// Readiness capture (T-0088) — the path that freezes the scorecard, so the
// trend and the recommendation history read the new point.
// ===========================================================================

export function captureReadinessSnapshot(
  engagementId: string,
  idempotencyKey: string,
): Promise<CaptureReadinessSnapshotResult> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/readiness/snapshots`,
    CaptureReadinessSnapshotResultSchema,
    { method: 'POST', body: {}, idempotencyKey },
  );
}

export function useCaptureReadinessSnapshotMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => captureReadinessSnapshot(engagementId!, newKey()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['readiness', 'trend', engagementId] });
      void qc.invalidateQueries({ queryKey: ['readiness', 'snapshots', engagementId] });
      void qc.invalidateQueries({ queryKey: ['snapshots', engagementId] });
    },
  });
}

/** An AI proposal still waiting for a person; 'accepted' / 'overridden' are confirmed. */
export function isProposalPending(status: string): boolean {
  return status === 'pending';
}
export function isProposalConfirmed(status: string): boolean {
  return status === 'accepted' || status === 'overridden';
}

// ===========================================================================
// Who may draft, publish and capture — the API floor is engagement_lead,
// which a firm admin clears too. Unknown (loading, error) is false; the
// write routes refuse on their own.
// ===========================================================================

export function useCanLeadHealthReadiness(): boolean {
  const role = useMeQuery().data?.role;
  return role === 'engagement_lead' || role === 'firm_admin';
}

// ===========================================================================
// Display vocabulary (the prototype's labels)
// ===========================================================================

export const ROLLUP_TO_POSTURE: Record<RollupColor, PostureStatus> = {
  red: 'critical',
  yellow_divergent: 'at-risk',
  green: 'healthy',
  grey: 'unassessed',
};

export const ROLLUP_LABEL: Record<RollupColor, string> = {
  red: 'Red',
  yellow_divergent: 'Amber',
  green: 'Green',
  grey: 'Not rated',
};

export const RECOMMENDATION_LABEL: Record<string, string> = {
  go: 'Go',
  conditional_go: 'Conditional',
  no_go: 'No-Go',
};

/**
 * What a readiness capture froze, in words: the total and the
 * recommendation of that moment. The strip and the Snapshots & trend pane
 * say the same thing.
 */
export function capturedSnapshotNotice(snap: CaptureReadinessSnapshotResult): string {
  const total = snap.total_percent === null ? 'not scored' : `${snap.total_percent}%`;
  const label = snap.recommendation ? RECOMMENDATION_LABEL[snap.recommendation] : undefined;
  return `Snapshot captured: ${total}${label ? ` ${label}` : ''}. Frozen; it never changes.`;
}

export const GO_LIVE_SECTION_DESCRIPTION =
  'The go-live readiness summary: the weighted score, the recommendation and the gaps behind it.';
